import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Alert, Typography, Space, Tag, Table, Modal, Form, Input, InputNumber, Switch, Upload, Tabs, Image, Select, Divider, List, Avatar, Progress, message } from 'antd';
import SimpleHeroBanners from './SimpleHeroBanners';
import { 
    FileTextOutlined, 
    PictureOutlined, 
    SettingOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    SaveOutlined,
    UploadOutlined,
    FolderOutlined,
    FileImageOutlined,
    VideoCameraOutlined,
    FileOutlined,
    SearchOutlined,
    FilterOutlined,
    GlobalOutlined,
    EditFilled,
    DeleteFilled
} from '@ant-design/icons';
import api from '../../../services/api';
import { resolveImageSrc } from '../../../utils/images';
import { useAuth } from '../../../contexts/AuthContext';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

function AdminContent() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('content');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Content Management
    const [contentItems, setContentItems] = useState([]);
    const [contentForm] = Form.useForm();
    const [contentModalVisible, setContentModalVisible] = useState(false);
    const [editingContent, setEditingContent] = useState(null);
    
    // Media Management
    const [mediaItems, setMediaItems] = useState([]);
    const [mediaForm] = Form.useForm();
    const [mediaModalVisible, setMediaModalVisible] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [mediaSearch, setMediaSearch] = useState('');
    
    // Filters
    const [contentFilter, setContentFilter] = useState({ section: '', type: '' });
    const [mediaFilter, setMediaFilter] = useState({ category: '', file_type: '' });

    useEffect(() => {
        loadContent();
        loadMedia();
    }, []);

    const loadContent = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetContent();
            console.log('Content API response:', response);
            const contentData = response.data?.data || response.data || [];
            console.log('Content items loaded:', contentData);
            setContentItems(contentData);
        } catch (e) {
            console.error('Failed to load content:', e);
            setError('Failed to load content: ' + (e.message || 'Unknown error'));
            setContentItems([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMedia = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetMedia({ search: mediaSearch });
            setMediaItems(response.data?.data || []);
        } catch (e) {
            setError('Failed to load media');
            // Disable mock fallback to avoid misleading admin data
            setMediaItems([]);
        } finally {
            setLoading(false);
        }
    };

    // Removed mock generators; keep structure in place if needed later with feature flags
    const getMockContent = () => [
        {
            id: 1,
            key: 'homepage_hero_title',
            type: 'text',
            content: 'India\'s No.1 Home Catering Platform',
            title: 'Homepage Hero Title',
            description: 'Main headline on the homepage',
            section: 'homepage',
            is_active: true,
            sort_order: 1,
            metadata: { font_size: 'large', color: 'primary' }
        },
        {
            id: 2,
            key: 'homepage_hero_subtitle',
            type: 'text',
            content: 'Discover delicious food, professional service, and unforgettable experiences for your special occasions.',
            title: 'Homepage Hero Subtitle',
            description: 'Subtitle text below the main headline',
            section: 'homepage',
            is_active: true,
            sort_order: 2,
            metadata: {}
        },
        {
            id: 3,
            key: 'homepage_hero_image',
            type: 'image',
            content: '/images/hero-catering.jpg',
            title: 'Homepage Hero Image',
            description: 'Main hero image on homepage',
            section: 'homepage',
            is_active: true,
            sort_order: 3,
            metadata: { alt_text: 'Catering service hero image', width: 1200, height: 600 }
        },
        {
            id: 4,
            key: 'footer_about_text',
            type: 'text',
            content: 'Spoon & Spice is India\'s premier catering platform, connecting customers with the best local caterers for their special occasions.',
            title: 'Footer About Text',
            description: 'About text in the footer',
            section: 'footer',
            is_active: true,
            sort_order: 1,
            metadata: {}
        },
        {
            id: 5,
            key: 'contact_phone',
            type: 'text',
            content: '+91 7483423925',
            title: 'Contact Phone Number',
            description: 'Main contact phone number',
            section: 'contact',
            is_active: true,
            sort_order: 1,
            metadata: { clickable: true }
        }
    ];

    const getMockMedia = () => [
        {
            id: 1,
            filename: 'hero-catering.jpg',
            original_name: 'hero-catering.jpg',
            file_path: 'media-library/hero-catering.jpg',
            file_type: 'image',
            mime_type: 'image/jpeg',
            file_size: 2048576,
            alt_text: 'Catering service hero image',
            description: 'Main hero image for homepage',
            category: 'banner',
            dimensions: { width: 1200, height: 600 },
            is_public: true,
            created_at: '2025-01-27T10:00:00Z'
        },
        {
            id: 2,
            filename: 'wedding-catering.jpg',
            original_name: 'wedding-catering.jpg',
            file_path: 'media-library/wedding-catering.jpg',
            file_type: 'image',
            mime_type: 'image/jpeg',
            file_size: 1536000,
            alt_text: 'Wedding catering service',
            description: 'Wedding catering showcase image',
            category: 'gallery',
            dimensions: { width: 800, height: 600 },
            is_public: true,
            created_at: '2025-01-27T09:30:00Z'
        }
    ];

    const openContentModal = (content = null) => {
        setEditingContent(content);
        if (content) {
            contentForm.setFieldsValue({
                key: content.key,
                type: content.type,
                content: content.content,
                title: content.title,
                description: content.description,
                section: content.section,
                is_active: content.is_active,
                sort_order: content.sort_order,
                metadata: content.metadata
            });
        } else {
            contentForm.resetFields();
        }
        setContentModalVisible(true);
    };

    const handleContentSave = async (values) => {
        try {
            setLoading(true);
            if (editingContent) {
                await api.adminUpdateContent(editingContent.id, values);
                message.success('Content updated successfully');
            } else {
                await api.adminCreateContent(values);
                message.success('Content created successfully');
            }
            setContentModalVisible(false);
            contentForm.resetFields();
            loadContent();
        } catch (e) {
            setError('Failed to save content');
        } finally {
            setLoading(false);
        }
    };

    const deleteContent = async (id) => {
        try {
            setLoading(true);
            await api.adminDeleteContent(id);
            message.success('Content deleted successfully');
            loadContent();
        } catch (e) {
            setError('Failed to delete content');
        } finally {
            setLoading(false);
        }
    };

    const handleMediaUpload = async (file) => {
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('category', 'general');
            formData.append('is_public', 'true');
            
            await api.adminUploadMedia(formData);
            message.success('File uploaded successfully');
            loadMedia();
        } catch (e) {
            message.error('Failed to upload file');
        } finally {
            setUploading(false);
        }
        return false; // Prevent default upload
    };

    const deleteMedia = async (id) => {
        try {
            setLoading(true);
            await api.adminDeleteMedia(id);
            message.success('Media deleted successfully');
            loadMedia();
        } catch (e) {
            setError('Failed to delete media');
        } finally {
            setLoading(false);
        }
    };

    const contentColumns = [
        {
            title: 'Content',
            key: 'content',
            render: (_, record) => (
                <div>
                    <Text strong>{record.title}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        Key: {record.key}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.description}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag color={type === 'image' ? 'blue' : type === 'html' ? 'green' : 'default'}>
                    {type.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Section',
            dataIndex: 'section',
            key: 'section',
            render: (section) => (
                <Tag color="purple">{section}</Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<EditOutlined />}
                        onClick={() => openContentModal(record)}
                    >
                        Edit
                    </Button>
                    <Button 
                        size="small" 
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => deleteContent(record.id)}
                        loading={loading}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    const mediaColumns = [
        {
            title: 'Media',
            key: 'media',
            render: (_, record) => (
                <Space>
                    {record.file_type === 'image' ? (
                        <Image
                            width={60}
                            height={40}
                            src={resolveImageSrc((record.variants && record.variants.thumb) || record.url || record.file_path)}
                            alt={record.alt_text}
                            style={{ objectFit: 'cover', borderRadius: 4 }}
                        />
                    ) : (
                        <Avatar 
                            size={40} 
                            icon={record.file_type === 'video' ? <VideoCameraOutlined /> : <FileOutlined />}
                        />
                    )}
                    <div>
                        <Text strong>{record.original_name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {record.file_size_human || '2.0 MB'}
                        </Text>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'file_type',
            key: 'file_type',
            render: (type) => (
                <Tag color={type === 'image' ? 'blue' : type === 'video' ? 'red' : 'default'}>
                    {(type || '').toString().toUpperCase() || '—'}
                </Tag>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category) => (
                <Tag color="purple">{category}</Tag>
            ),
        },
        {
            title: 'Dimensions',
            key: 'dimensions',
            render: (_, record) => (
                record.dimensions ? (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.dimensions.width} × {record.dimensions.height}
                    </Text>
                ) : '-'
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button 
                        size="small" 
                        onClick={() => navigator.clipboard.writeText(resolveImageSrc(record.url || record.file_path)).then(() => message.success('URL copied'))}
                    >
                        Copy URL
                    </Button>
                    <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        href={resolveImageSrc(record.url || record.file_path)}
                        target="_blank"
                    >
                        View
                    </Button>
                    <Button size="small" onClick={() => message.info('Replace coming soon')}>Replace</Button>
                    <Button 
                        size="small" 
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => deleteMedia(record.id)}
                        loading={loading}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    const filteredContent = contentItems.filter(item => {
        if (contentFilter.section && item.section !== contentFilter.section) return false;
        if (contentFilter.type && item.type !== contentFilter.type) return false;
        return true;
    });

    const filteredMedia = mediaItems.filter(item => {
        if (mediaFilter.category && item.category !== mediaFilter.category) return false;
        if (mediaFilter.file_type && item.file_type !== mediaFilter.file_type) return false;
        return true;
    });

    const contentSections = [...new Set(contentItems.map(item => item.section))];
    const mediaCategories = [...new Set(mediaItems.map(item => item.category))];

    if (user && user.user_type !== 'admin') {
        return (
            <Alert type="error" message="Access denied" description="You do not have permission to view this page." />
        );
    }

    return (
        <div>
            <div className="mb-6">
                <Title level={2}>Content & Media Management</Title>
                <Paragraph type="secondary">
                    Manage all website content, images, and media files dynamically.
                </Paragraph>
            </div>

            {error && (
                <Alert 
                    type="error" 
                    message={error} 
                    className="mb-6" 
                    closable 
                    onClose={() => setError('')}
                />
            )}

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab="Dynamic Content" key="content">
                    <Card 
                        title="Website Content" 
                        extra={
                            <Space>
                                <Select
                                    placeholder="Filter by section"
                                    style={{ width: 150 }}
                                    allowClear
                                    onChange={(value) => setContentFilter(prev => ({ ...prev, section: value }))}
                                >
                                    {contentSections.map(section => (
                                        <Option key={section} value={section}>{section}</Option>
                                    ))}
                                </Select>
                                <Select
                                    placeholder="Filter by type"
                                    style={{ width: 120 }}
                                    allowClear
                                    onChange={(value) => setContentFilter(prev => ({ ...prev, type: value }))}
                                >
                                    <Option value="text">Text</Option>
                                    <Option value="image">Image</Option>
                                    <Option value="html">HTML</Option>
                                    <Option value="json">JSON</Option>
                                </Select>
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />}
                                    onClick={() => openContentModal()}
                                >
                                    Add Content
                                </Button>
                            </Space>
                        }
                    >
                        <Table
                            columns={contentColumns}
                            dataSource={filteredContent}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} content items`
                            }}
                            scroll={{ x: true }}
                        />
                    </Card>
                </TabPane>

                <TabPane tab="Hero & Banners" key="hero_banners">
                    <SimpleHeroBanners />
                </TabPane>

                <TabPane tab="Media Library" key="media">
                    <Card 
                        title="Media Library" 
                        extra={
                            <Space>
                                <Select
                                    placeholder="Filter by category"
                                    style={{ width: 150 }}
                                    allowClear
                                    onChange={(value) => setMediaFilter(prev => ({ ...prev, category: value }))}
                                >
                                    {mediaCategories.map(category => (
                                        <Option key={category} value={category}>{category}</Option>
                                    ))}
                                </Select>
                                <Select
                                    placeholder="Filter by type"
                                    style={{ width: 120 }}
                                    allowClear
                                    onChange={(value) => setMediaFilter(prev => ({ ...prev, file_type: value }))}
                                >
                                    <Option value="image">Image</Option>
                                    <Option value="video">Video</Option>
                                    <Option value="document">Document</Option>
                                </Select>
                                <Input 
                                    placeholder="Search media..." 
                                    style={{ width: 200 }}
                                    value={mediaSearch}
                                    onChange={(e) => setMediaSearch(e.target.value)}
                                    onPressEnter={loadMedia}
                                />
                                <Button onClick={loadMedia} icon={<SearchOutlined />}>Search</Button>
                                <Upload
                                    beforeUpload={handleMediaUpload}
                                    showUploadList={false}
                                    accept="image/*,video/*,.pdf,.doc,.docx"
                                >
                                    <Button 
                                        type="primary" 
                                        icon={<UploadOutlined />}
                                        loading={uploading}
                                    >
                                        Upload Media
                                    </Button>
                                </Upload>
                            </Space>
                        }
                    >
                        <Table
                            columns={mediaColumns}
                            dataSource={filteredMedia}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                pageSize: 12,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} media files`
                            }}
                            scroll={{ x: true }}
                        />
                    </Card>
                </TabPane>

                <TabPane tab="SEO & Meta" key="seo">
                    <Card title="SEO & Meta Tags">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={12}>
                                <Card size="small" title="Homepage SEO">
                                    <Form layout="vertical">
                                        <Form.Item label="Meta Title">
                                            <Input placeholder="Spoon & Spice - India's No.1 Catering Platform" />
                                        </Form.Item>
                                        <Form.Item label="Meta Description">
                                            <TextArea rows={3} placeholder="Discover delicious food, professional service..." />
                                        </Form.Item>
                                        <Form.Item label="Keywords">
                                            <Input placeholder="catering, food delivery, wedding catering" />
                                        </Form.Item>
                                        <Button type="primary">Save SEO Settings</Button>
                                    </Form>
                                </Card>
                            </Col>
                            <Col xs={24} lg={12}>
                                <Card size="small" title="Social Media">
                                    <Form layout="vertical">
                                        <Form.Item label="Open Graph Title">
                                            <Input placeholder="Spoon & Spice - Premium Catering" />
                                        </Form.Item>
                                        <Form.Item label="Open Graph Description">
                                            <TextArea rows={3} placeholder="Your special occasions deserve the best..." />
                                        </Form.Item>
                                        <Form.Item label="Twitter Card">
                                            <Select defaultValue="summary_large_image">
                                                <Option value="summary">Summary</Option>
                                                <Option value="summary_large_image">Summary Large Image</Option>
                                            </Select>
                                        </Form.Item>
                                        <Button type="primary">Save Social Settings</Button>
                                    </Form>
                                </Card>
                            </Col>
                        </Row>
                    </Card>
                </TabPane>
            </Tabs>

            {/* Content Modal */}
            <Modal
                title={editingContent ? 'Edit Content' : 'Add Content'}
                open={contentModalVisible}
                onCancel={() => setContentModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={contentForm}
                    layout="vertical"
                    onFinish={handleContentSave}
                >
                    <Form.Item
                        label="Content Key"
                        name="key"
                        rules={[{ required: true, message: 'Please enter content key' }]}
                    >
                        <Input placeholder="e.g., homepage_hero_title" />
                    </Form.Item>

                    <Form.Item
                        label="Title"
                        name="title"
                        rules={[{ required: true, message: 'Please enter title' }]}
                    >
                        <Input placeholder="Human readable title" />
                    </Form.Item>

                    <Form.Item
                        label="Description"
                        name="description"
                    >
                        <TextArea rows={2} placeholder="Description for admin reference" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Type"
                                name="type"
                                rules={[{ required: true, message: 'Please select type' }]}
                            >
                                <Select>
                                    <Option value="text">Text</Option>
                                    <Option value="image">Image</Option>
                                    <Option value="html">HTML</Option>
                                    <Option value="json">JSON</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Section"
                                name="section"
                                rules={[{ required: true, message: 'Please select section' }]}
                            >
                                <Select>
                                    <Option value="homepage">Homepage</Option>
                                    <Option value="footer">Footer</Option>
                                    <Option value="header">Header</Option>
                                    <Option value="contact">Contact</Option>
                                    <Option value="about">About</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Content input adapts to type */}
                    <Form.Item shouldUpdate={(prev, cur) => prev.type !== cur.type} noStyle>
                        {() => {
                            const currentType = contentForm.getFieldValue('type');
                            if (currentType === 'image') {
                                const handleInlineImageUpload = async (file) => {
                                    try {
                                        const formData = new FormData();
                                        formData.append('file', file);
                                        formData.append('category', 'general');
                                        formData.append('is_public', 'true');
                                        const res = await api.adminUploadMedia(formData);
                                        if (res && res.success && res.data) {
                                            const newItem = res.data;
                                            const url = resolveImageSrc(newItem.url || newItem.file_path);
                                            // prepend to media list
                                            setMediaItems(prev => [newItem, ...(prev || [])]);
                                            contentForm.setFieldsValue({
                                                content: url,
                                                metadata: {
                                                    ...(editingContent?.metadata || {}),
                                                    media_id: newItem.id,
                                                    alt_text: newItem.alt_text || ''
                                                }
                                            });
                                            message.success('Image uploaded');
                                        }
                                    } catch (e) {
                                        message.error('Upload failed');
                                    }
                                    return false; // prevent auto upload
                                };
                                const imageOptions = (mediaItems || []).filter(m => m.file_type === 'image');
                                return (
                                    <>
                                        <Form.Item label="Upload New Image">
                                            <Upload 
                                                beforeUpload={handleInlineImageUpload}
                                                showUploadList={false}
                                                accept="image/*"
                                            >
                                                <Button icon={<UploadOutlined />}>Upload Image</Button>
                                            </Upload>
                                        </Form.Item>
                                        <Form.Item label="Select Image from Media Library" required>
                                            <Select
                                                showSearch
                                                placeholder="Choose an image"
                                                optionFilterProp="label"
                                                onChange={(value, option) => {
                                                    const selected = imageOptions.find(m => resolveImageSrc(m.url || m.file_path) === value);
                                                    contentForm.setFieldsValue({
                                                        content: value,
                                                        metadata: {
                                                            ...(editingContent?.metadata || {}),
                                                            media_id: selected?.id || null,
                                                            alt_text: selected?.alt_text || '',
                                                        }
                                                    });
                                                }}
                                                options={imageOptions.map(m => ({
                                                    value: resolveImageSrc(m.url || m.file_path),
                                                    label: m.original_name || m.filename,
                                                }))}
                                            />
                                        </Form.Item>
                                        <Form.Item label="Content (Image URL)" name="content" rules={[{ required: true, message: 'Please select an image' }]}>
                                            <Input placeholder="Image URL" />
                                        </Form.Item>
                                    </>
                                );
                            }
                            return (
                                <Form.Item
                                    label="Content"
                                    name="content"
                                    rules={[{ required: true, message: 'Please enter content' }]}
                                >
                                    <TextArea rows={4} placeholder="Enter the content value" />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>

                    {/* Hidden metadata support to store media_id, alt_text, etc. */}
                    <Form.Item name="metadata" hidden>
                        <span />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Sort Order"
                                name="sort_order"
                                initialValue={0}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Active"
                                name="is_active"
                                valuePropName="checked"
                                initialValue={true}
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider />

                    <div className="flex justify-end gap-2">
                        <Button onClick={() => setContentModalVisible(false)}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            {editingContent ? 'Update' : 'Create'} Content
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
}

export default AdminContent;